import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import {
  getQueryData,
  Query,
  setQueryData,
  useMutation,
  useQuery,
} from '@globalconnect/query';
import { Observable } from 'rxjs';
import { FeedbackAlertService } from '../../shared/feedback-alert/service/feedback-alert.service';
import { sharepointExportKey } from '../entities/query-keys';
import {
  ReportTypes,
  SharepointExportResponse,
  SharepointExportResponseQueryLoadingState,
  SharepointExportResponseQueryOptions,
} from '../entities/sharepoint-export-entities';
import { SharepointExportApiService } from './sharepoint-export-api.service';

@Injectable({
  providedIn: 'root',
})
export class SharepointExportService {
  private readonly sharepointExportApiService = inject(
    SharepointExportApiService,
  );
  private readonly feedbackAlertService = inject(FeedbackAlertService);

  private readonly _ifsNumber = signal<string>('');
  readonly ifsNumber = this._ifsNumber.asReadonly();

  private readonly _locale = signal<string>('');
  readonly locale = this._locale.asReadonly();

  private readonly _loadingExportReportTypes = signal<ReportTypes[]>([]);
  readonly loadingExportReportTypes =
    this._loadingExportReportTypes.asReadonly();

  private readonly _isHelpModalOpen = signal(false);
  readonly isHelpModalOpen = this._isHelpModalOpen.asReadonly();

  private readonly _isLoadingConstructionReports = signal(true);
  readonly isLoadingConstructionReports =
    this._isLoadingConstructionReports.asReadonly();

  private readonly _isRefreshingHcConstructionReports = signal(false);
  readonly isRefreshingHcConstructionReports =
    this._isRefreshingHcConstructionReports.asReadonly();

  getConstructionReports$(
    options?: Partial<SharepointExportResponseQueryOptions>,
  ): Observable<SharepointExportResponseQueryLoadingState> {
    this.setIsLoadingConstructionReports(true);
    const query = useQuery(
      [sharepointExportKey, this.ifsNumber()],
      () =>
        this.sharepointExportApiService.getConstructionReports$(
          this.ifsNumber(),
        ),
      {
        ...options,
        cacheTime: 0,
        staleTime: 0,
        onSuccess: () => {
          this.setIsLoadingConstructionReports(false);
        },
        onError: () => {
          this.setIsLoadingConstructionReports(false);
        },
      },
    ).getState$();
    setQueryData([sharepointExportKey, this.ifsNumber()], () => query);
    return query;
  }

  getConstructionReportsQueryData$(
    ifsNumber: string,
  ):
    | Query<
        SharepointExportResponse,
        HttpErrorResponse,
        string,
        SharepointExportResponse
      >
    | undefined {
    return getQueryData([sharepointExportKey, ifsNumber]);
  }

  exportReports$ = (reportTypes: ReportTypes[]): void =>
    useMutation(
      () =>
        this.sharepointExportApiService.exportReports$(this.ifsNumber(), {
          locale: this.locale(),
          reportTypes,
        }),
      {
        onSuccess: () => {
          this.removeLoadingExportReportTypes(reportTypes);
          this.feedbackAlertService.showAlert(
            'success',
            'sharepointExport.alerts.exportStarted',
          );
          this.getConstructionReports$();
        },
        onError: (err) => {
          this.removeLoadingExportReportTypes(reportTypes);
          this.feedbackAlertService.showAlert(
            'critical',
            'sharepointExport.alerts.exportFailed',
          );
          console.error(err);
        },
      },
    ).mutate();

  setIfsNumber = (ifsNumber: string) => {
    this._ifsNumber.set(ifsNumber);
  };

  setLocale = (locale: string) => {
    this._locale.set(locale);
  };

  setIsLoadingConstructionReports = (value: boolean) => {
    this._isLoadingConstructionReports.set(value);
  };

  setIsRefreshingHcConstructionReports = (value: boolean): void => {
    this._isRefreshingHcConstructionReports.set(value);
  };

  setIsHelpModalOpen = (state: boolean) => {
    this._isHelpModalOpen.set(state);
  };

  addLoadingExportReportTypes = (reportTypes: ReportTypes[]): void => {
    const currentLoadingReportTypes = this._loadingExportReportTypes();
    const mergedLoadingReportTypes = [
      ...new Set([...currentLoadingReportTypes, ...reportTypes]),
    ];
    this._loadingExportReportTypes.set(mergedLoadingReportTypes);
  };

  removeLoadingExportReportTypes = (reportTypes: ReportTypes[]): void => {
    const currentLoadingReportTypes = this._loadingExportReportTypes();
    const filteredLoadingReportTypes = currentLoadingReportTypes.filter(
      (reportType) => !reportTypes.includes(reportType),
    );
    this._loadingExportReportTypes.set(filteredLoadingReportTypes);
  };

  refreshHcConstructionReports$ = (): void =>
    useMutation(
      () =>
        this.sharepointExportApiService.refreshHcConstructionReports$(
          this.ifsNumber(),
        ),
      {
        onSuccess: () => {
          this.setIsRefreshingHcConstructionReports(false);

          this.feedbackAlertService.showAlert(
            'success',
            'sharepointExport.alerts.refreshSuccess',
          );

          this.getConstructionReports$();
        },
        onError: (err) => {
          this.setIsRefreshingHcConstructionReports(false);
          this.setIsLoadingConstructionReports(false);

          this.feedbackAlertService.showAlert(
            'critical',
            'sharepointExport.alerts.refreshFailed',
          );

          console.error(err);
        },
      },
    ).mutate();
}
